/**
 * Healthcare Design Points System (HDPS) Scoring Engine.
 * Adapted from Brain Health Points System (BHPS) for hospital environments.
 *
 * 100 points across 8 pathways, reweighted for healthcare priorities.
 * 7 mandatory preconditions (stricter thresholds) + 47 features earning 1-3 points each.
 *
 * Key differences from BHPS:
 * - Air Quality bumped to 15 pts (infection control critical)
 * - Nature bumped to 15 pts (Ulrich's seminal evidence is hospital-based)
 * - Movement reduced to 10 pts (limited patient mobility)
 * - Enrichment reduced to 10 pts (less relevant for acute care)
 * - Stricter preconditions: noise <45 dB (WHO hospital), PM2.5 <12 (EPA), +ASHRAE 170 ACH
 */

// Pathway caps (reweighted for healthcare)
export const HC_PATHWAY_CAPS = {
  daylight: 15,
  noise: 15,
  air_quality: 15,
  nature: 15,
  movement: 10,
  enrichment: 10,
  wayfinding: 10,
  visual: 10,
};

// Preconditions (7 mandatory, 0 pts — stricter for healthcare)
export const HC_PRECONDITIONS = [
  { id: 'P1', pathway: 'daylight', label: 'Minimum daylight >=150 lux in patient areas', threshold: '>=150 lux' },
  { id: 'P2', pathway: 'noise', label: 'Noise <45 dB in patient rooms (WHO hospital guideline)', threshold: '<45 dB' },
  { id: 'P3', pathway: 'air_quality', label: 'PM2.5 <12 ug/m3 (EPA standard)', threshold: '<12 ug/m3' },
  { id: 'P4', pathway: 'visual', label: 'Visual pattern stress <0.75', threshold: '<0.75' },
  { id: 'P5', pathway: 'wayfinding', label: 'Emergency wayfinding: code routes, exit signs visible', threshold: 'manual' },
  { id: 'P6', pathway: 'movement', label: 'ADA + gurney/wheelchair accessible circulation', threshold: 'manual' },
  { id: 'P7', pathway: 'air_quality', label: 'Minimum 6 ACH in patient rooms (ASHRAE 170)', threshold: 'manual' },
];

// Feature catalog (47 features)
export const HC_FEATURES = [
  // Daylight (15 pts)
  { id: 'D1', pathway: 'daylight', points: 3, label: 'Ambient light >=300 lux at patient bed', autoCheck: s => s.daylight_lux >= 300 },
  { id: 'D2', pathway: 'daylight', points: 3, label: 'Ambient light >=500 lux or direct sky view from patient areas', autoCheck: s => s.daylight_lux >= 500 },
  { id: 'D3', pathway: 'daylight', points: 2, label: 'Tunable color temperature (2700-5000K) in patient rooms', autoCheck: null },
  { id: 'D4', pathway: 'daylight', points: 2, label: 'Circadian-aligned lighting schedule (bright days, dim evenings)', autoCheck: null },
  { id: 'D5', pathway: 'daylight', points: 2, label: 'Dedicated task lighting >=500 lux at nursing stations', autoCheck: null },
  { id: 'D6', pathway: 'daylight', points: 1, label: 'Light therapy station for extended-stay patients', autoCheck: null },
  { id: 'D7', pathway: 'daylight', points: 2, label: 'Automated daylight-responsive dimming with nighttime protocol', autoCheck: null },

  // Noise (15 pts)
  { id: 'N1', pathway: 'noise', points: 3, label: 'Ambient noise <=45 dB in patient rooms', autoCheck: s => s.noise_db <= 45 },
  { id: 'N2', pathway: 'noise', points: 3, label: 'Ambient noise <=40 dB during nighttime (10pm-6am)', autoCheck: s => s.noise_db <= 40 },
  { id: 'N3', pathway: 'noise', points: 3, label: 'Acoustic ceiling (NRC >=0.75, >=60% coverage in patient corridors)', autoCheck: null },
  { id: 'N4', pathway: 'noise', points: 2, label: 'Silent closing mechanisms on all patient room doors', autoCheck: null },
  { id: 'N5', pathway: 'noise', points: 1, label: 'Alarm management protocol (tiered alerts, visual notifications)', autoCheck: null },
  { id: 'N6', pathway: 'noise', points: 2, label: 'Sound masking system in open areas (nurse stations, waiting)', autoCheck: null },
  { id: 'N7', pathway: 'noise', points: 1, label: 'Acoustic privacy for patient consultations (STC >=45)', autoCheck: null },

  // Air Quality (15 pts)
  { id: 'A1', pathway: 'air_quality', points: 2, label: 'PM2.5 <10 ug/m3 annual average', autoCheck: s => s.pm25_ug_m3 < 10 },
  { id: 'A2', pathway: 'air_quality', points: 2, label: 'PM2.5 <5 ug/m3 annual average (WHO guideline)', autoCheck: s => s.pm25_ug_m3 < 5 },
  { id: 'A3', pathway: 'air_quality', points: 3, label: 'HEPA filtration (MERV 17+) in patient areas', autoCheck: null },
  { id: 'A4', pathway: 'air_quality', points: 2, label: '12+ ACH in procedure rooms (ASHRAE 170 enhanced)', autoCheck: null },
  { id: 'A5', pathway: 'air_quality', points: 2, label: 'UV-C air disinfection in HVAC or upper room', autoCheck: null },
  { id: 'A6', pathway: 'air_quality', points: 2, label: 'Positive/negative pressure isolation rooms per CDC guidelines', autoCheck: null },
  { id: 'A7', pathway: 'air_quality', points: 1, label: 'Real-time air quality monitoring with staff alerts', autoCheck: null },
  { id: 'A8', pathway: 'air_quality', points: 1, label: 'Antimicrobial copper touch surfaces in high-traffic areas', autoCheck: null },

  // Nature/Biophilia (15 pts)
  { id: 'B1', pathway: 'nature', points: 3, label: 'Direct nature view from >=50% of patient beds', autoCheck: s => s.nature_view },
  { id: 'B2', pathway: 'nature', points: 3, label: 'Accessible healing garden within 2 min of patient areas', autoCheck: null },
  { id: 'B3', pathway: 'nature', points: 2, label: 'Indoor plants >=1 per 200 sq ft (infection-safe species)', autoCheck: null },
  { id: 'B4', pathway: 'nature', points: 2, label: 'Natural materials (wood, stone) in patient room finishes', autoCheck: null },
  { id: 'B5', pathway: 'nature', points: 2, label: 'Nature imagery or murals in corridors and waiting areas', autoCheck: null },
  { id: 'B6', pathway: 'nature', points: 1, label: 'Water feature in public areas (non-aerosolizing)', autoCheck: null },
  { id: 'B7', pathway: 'nature', points: 2, label: 'Biomorphic/organic design forms in patient areas', autoCheck: null },

  // Movement (10 pts)
  { id: 'M1', pathway: 'movement', points: 2, label: 'Walking route >=30m corridor loop for patient ambulation', autoCheck: s => s.movement_minutes > 0 },
  { id: 'M2', pathway: 'movement', points: 2, label: 'Handrail-equipped walking paths with distance markers', autoCheck: null },
  { id: 'M3', pathway: 'movement', points: 2, label: 'Standing-height work surfaces at nurse stations', autoCheck: null },
  { id: 'M4', pathway: 'movement', points: 2, label: 'Rehabilitation/exercise space within unit or 2 min walk', autoCheck: null },
  { id: 'M5', pathway: 'movement', points: 2, label: 'Direct outdoor access/patio for ambulatory patients', autoCheck: s => s.nature_exposure },

  // Enrichment (10 pts)
  { id: 'E1', pathway: 'enrichment', points: 2, label: 'Visual interest: artwork, color variation in patient corridors', autoCheck: null },
  { id: 'E2', pathway: 'enrichment', points: 2, label: 'Spatial diversity: distinct zones for rest, activity, socializing', autoCheck: null },
  { id: 'E3', pathway: 'enrichment', points: 2, label: 'Sensory variety: varied textures, ambient sound options', autoCheck: null },
  { id: 'E4', pathway: 'enrichment', points: 2, label: 'Social affordances: family seating areas, dayrooms', autoCheck: null },
  { id: 'E5', pathway: 'enrichment', points: 2, label: 'Cognitive engagement: interactive displays, reading materials', autoCheck: null },

  // Wayfinding (10 pts)
  { id: 'W1', pathway: 'wayfinding', points: 2, label: 'Clear directional signage at every decision point', autoCheck: null },
  { id: 'W2', pathway: 'wayfinding', points: 2, label: 'Color-coded wayfinding by department/floor', autoCheck: null },
  { id: 'W3', pathway: 'wayfinding', points: 2, label: 'Visual sightlines from patient rooms to nurse station', autoCheck: s => s.wayfinding_score >= 0.7 },
  { id: 'W4', pathway: 'wayfinding', points: 2, label: 'Digital/interactive wayfinding kiosks at entries', autoCheck: null },
  { id: 'W5', pathway: 'wayfinding', points: 1, label: 'Multilingual signage in top 3 community languages', autoCheck: null },
  { id: 'W6', pathway: 'wayfinding', points: 1, label: 'You-are-here maps with emergency routes at all entries', autoCheck: null },

  // Visual Comfort (10 pts)
  { id: 'V1', pathway: 'visual', points: 3, label: 'No uniform grid patterns on major surfaces in patient areas', autoCheck: s => s.visual_pattern_stress < 0.3 },
  { id: 'V2', pathway: 'visual', points: 2, label: 'Natural/organic patterns on ceiling tiles and walls', autoCheck: null },
  { id: 'V3', pathway: 'visual', points: 2, label: 'Non-reflective, matte surface materials on floors', autoCheck: null },
  { id: 'V4', pathway: 'visual', points: 2, label: 'Non-uniform, warm lighting layout in patient rooms', autoCheck: null },
  { id: 'V5', pathway: 'visual', points: 1, label: 'Glare control on all patient-facing windows', autoCheck: null },
];

export const HC_FEATURE_MAP = Object.fromEntries(HC_FEATURES.map(f => [f.id, f]));

/**
 * Check all 7 preconditions against a space (healthcare-strict thresholds).
 */
export function checkHcPreconditions(space) {
  return {
    P1: (space.daylight_lux || 0) >= 150,
    P2: (space.noise_db || 999) < 45,       // Stricter: 45 dB (WHO hospital)
    P3: (space.pm25_ug_m3 || 999) < 12,     // Stricter: 12 ug/m3 (EPA)
    P4: (space.visual_pattern_stress || 0) < 0.75,
    P5: true, // Manual: emergency wayfinding
    P6: true, // Manual: ADA + gurney accessible
    P7: true, // Manual: 6 ACH (ASHRAE 170)
  };
}

/**
 * Auto-detect features from space measurements.
 */
export function autoDetectHcFeatures(space) {
  const detected = {};
  for (const f of HC_FEATURES) {
    if (f.autoCheck) {
      try {
        detected[f.id] = f.autoCheck(space);
      } catch {
        detected[f.id] = false;
      }
    }
  }
  return detected;
}

/**
 * Score a single space using HDPS.
 */
export function scoreHcSpace(space, featureStatuses = {}) {
  const preconditions = checkHcPreconditions(space);
  const allPass = Object.values(preconditions).every(v => v);
  const auto = autoDetectHcFeatures(space);

  const pathwayPoints = {};
  for (const pw of Object.keys(HC_PATHWAY_CAPS)) {
    pathwayPoints[pw] = 0;
  }

  const features = HC_FEATURES.map(f => {
    const manualStatus = featureStatuses[f.id] || 'not_pursuing';
    const autoResult = auto[f.id];

    let status, autoDetected;
    if (manualStatus === 'earned') {
      status = 'earned';
      autoDetected = false;
    } else if (autoResult === true) {
      status = 'earned';
      autoDetected = true;
    } else {
      status = manualStatus;
      autoDetected = false;
    }

    let earnedPts = status === 'earned' ? f.points : 0;
    if (pathwayPoints[f.pathway] + earnedPts > HC_PATHWAY_CAPS[f.pathway]) {
      earnedPts = Math.max(0, HC_PATHWAY_CAPS[f.pathway] - pathwayPoints[f.pathway]);
    }
    pathwayPoints[f.pathway] += earnedPts;

    return {
      featureId: f.id,
      status,
      points: earnedPts,
      label: f.label,
      pathway: f.pathway,
      autoDetected,
    };
  });

  const totalPoints = Object.values(pathwayPoints).reduce((a, b) => a + b, 0);
  const tier = allPass ? getHcCertificationTier(totalPoints) : 'none';

  return {
    spaceId: space.id,
    spaceName: space.name,
    preconditionsPassed: allPass,
    preconditionResults: preconditions,
    totalPoints,
    pathwayPoints,
    pathwayCaps: HC_PATHWAY_CAPS,
    tier,
    features,
  };
}

/**
 * Score an entire healthcare project (WELL/LEED-style tiered-threshold scoring).
 */
export function scoreHcProject(spacesWithFeatures) {
  const numSpaces = spacesWithFeatures.length;
  if (numSpaces === 0) {
    return {
      totalPoints: 0, pathwayPoints: {}, pathwayCaps: HC_PATHWAY_CAPS,
      tier: 'none', allPreconditionsPass: false, projectFeatures: [],
      spaceScores: [], avgScore: 0,
    };
  }

  const spaceScores = spacesWithFeatures.map(({ space, featureStatuses }) =>
    scoreHcSpace(space, featureStatuses)
  );

  const allPreconditionsPass = spaceScores.every(ss => ss.preconditionsPassed);

  const featureEarnedCounts = {};
  for (const f of HC_FEATURES) {
    featureEarnedCounts[f.id] = spaceScores.filter(ss => {
      const sf = ss.features.find(feat => feat.featureId === f.id);
      return sf && sf.status === 'earned';
    }).length;
  }

  const pathwayPoints = {};
  for (const pw of Object.keys(HC_PATHWAY_CAPS)) {
    pathwayPoints[pw] = 0;
  }

  const projectFeatures = HC_FEATURES.map(f => {
    const earnedCount = featureEarnedCounts[f.id] || 0;
    const pctEarned = earnedCount / numSpaces;
    const isAutoDetectable = f.autoCheck !== null;

    let earnedPts = 0;
    let complianceLevel = 'none';

    if (isAutoDetectable) {
      if (pctEarned >= 0.75) {
        earnedPts = f.points;
        complianceLevel = 'full';
      } else if (pctEarned >= 0.50) {
        earnedPts = Math.ceil(f.points / 2);
        complianceLevel = 'partial';
      }
    } else {
      if (earnedCount > 0) {
        earnedPts = f.points;
        complianceLevel = 'full';
      }
    }

    if (pathwayPoints[f.pathway] + earnedPts > HC_PATHWAY_CAPS[f.pathway]) {
      earnedPts = Math.max(0, HC_PATHWAY_CAPS[f.pathway] - pathwayPoints[f.pathway]);
    }
    pathwayPoints[f.pathway] += earnedPts;

    return {
      featureId: f.id,
      label: f.label,
      pathway: f.pathway,
      maxPoints: f.points,
      earnedPts,
      earnedCount,
      totalSpaces: numSpaces,
      pctCompliant: Math.round(pctEarned * 100),
      complianceLevel,
      isAutoDetectable,
    };
  });

  const totalPoints = Object.values(pathwayPoints).reduce((a, b) => a + b, 0);
  const tier = allPreconditionsPass ? getHcCertificationTier(totalPoints) : 'none';

  return {
    totalPoints,
    pathwayPoints,
    pathwayCaps: HC_PATHWAY_CAPS,
    tier,
    allPreconditionsPass,
    projectFeatures,
    spaceScores,
    avgScore: totalPoints,
  };
}

/**
 * Get HDPS certification tier from score.
 */
export function getHcCertificationTier(score) {
  score = Math.round(score);
  if (score >= 75) return 'platinum';
  if (score >= 60) return 'gold';
  if (score >= 45) return 'silver';
  if (score >= 30) return 'bronze';
  return 'none';
}
